import React from 'react';
import { Chip } from '../../../../components/Chip';
import { DeprecatedPanel } from '../../../../components/DeprecatedPanel';
import { Avatar } from '../../../../components/Avatar';
import { Application, ExpectedSalary } from '../../../../entities/Application';
import { AverageRating } from './AverageRating';
import { PortfolioAndCVAndCoverLetter } from './PortfolioAndCVAndCoverLetter';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { Icon } from '../../../../components/Icon';
import { humanize } from '../../../../utils/humanize';
import { getLocationString } from '../../../../components/LocationSelect';
import { formatSalary } from '../../../../utils/formatSalary';

interface PropTypes {
  application: Application;
}

function workingVisaValue(hasWorkingVisa?: boolean) {
  if (hasWorkingVisa == null) return null;

  return hasWorkingVisa ? 'Yes' : 'No';
}

function Entry(props: { label: string; value: string }) {
  if (props.value == null || props.value == '') return null;
  return (
    <div className='d-flex align-items-center'>
      <span className='me-auto'>{props.label}</span>
      <Chip className='text-primary bg-gray fw-semibold fs-6'>
        {props.value}
      </Chip>
    </div>
  );
}

function IconEntry(props: { icon: string; children: React.ReactNode }) {
  return (
    <div className='d-flex align-items-center'>
      <div className='fs-4'>
        <Icon name={props.icon} className='text-dark-200 me-2' />
      </div>
      <div className='text-truncate'>{props.children}</div>
    </div>
  );
}

function ExpectedSalaryEntry(props: { expectedSalary: ExpectedSalary }) {
  if (!props.expectedSalary) return null;

  const expectedSalary = formatSalary(
    props.expectedSalary.value,
    props.expectedSalary.currency,
  );

  return <Entry label='Expected Salary' value={expectedSalary} />;
}
export function CandidatePanel(props: PropTypes) {
  const averageRating = props.application.rating;
  const location = getLocationString(props.application.candidate);

  return (
    <DeprecatedPanel className={classNames('p-4 h5 mb-0', styles.panel)}>
      <div className='d-flex flex-column'>
        <div className='d-flex mb-4 justify-content-between'>
          <Avatar name={props.application.candidate.name} size='xl' />
          <AverageRating value={averageRating} />
        </div>
        <div className=''>
          <PortfolioAndCVAndCoverLetter application={props.application} />
        </div>
        <hr />
        <div className='d-flex flex-column gap-3'>
          {props.application.candidate.phone_number && (
            <IconEntry icon='bi-telephone'>
              {props.application.candidate.phone_number}
            </IconEntry>
          )}
          {props.application.candidate.email && (
            <IconEntry icon='bi-envelope'>
              <a
                className='text-info'
                href={`mailto:${props.application.candidate.email}`}
              >
                {props.application.candidate.email}
              </a>
            </IconEntry>
          )}
          {props.application.candidate.linkedin_profile_url && (
            <IconEntry icon='bi-linkedin'>
              <a
                className='text-info'
                href={props.application.candidate.linkedin_profile_url}
                target='_blank'
                rel='noreferrer'
              >
                View Linkedin
              </a>
            </IconEntry>
          )}
          {location && <IconEntry icon='bi-pin-map'>{location}</IconEntry>}
        </div>
        {(props.application.academicDegree || props.application.experience) && (
          <hr />
        )}
        <div className='d-flex flex-column gap-3'>
          {props.application.academicDegree && (
            <Entry
              label='Academic Degree'
              value={props.application.academicDegree}
            />
          )}
          {props.application.experience && (
            <Entry
              label='Experience'
              value={`${props.application.experience} years`}
            />
          )}
        </div>
        {(props.application.currentCompany ||
          props.application.currentTitle ||
          props.application.hasWorkingVisa != null) && <hr />}
        <div className='d-flex flex-column gap-3'>
          <Entry
            label='Current Company'
            value={props.application.currentCompany}
          />
          <Entry label='Job Title' value={props.application.currentTitle} />
          <Entry
            label='Working Visa'
            value={workingVisaValue(props.application.hasWorkingVisa)}
          />
          <ExpectedSalaryEntry
            expectedSalary={props.application.expectedSalary}
          />
        </div>
        {(props.application.source?.name ||
          props.application.source?.channelType) && <hr />}
        <div className='d-flex flex-column gap-3'>
          <Entry
            label='Channel'
            value={humanize(props.application.source?.channelType)}
          />
          <Entry label='Source' value={props.application.source?.displayName} />
        </div>
      </div>
    </DeprecatedPanel>
  );
}
